// ============ LANDING / PRICING / PREMIUM GATE / MOBILE NAV ============
import React, { useState, useRef, useEffect } from 'react';
import { NAV, NavIcon } from './core';
import { mmIsPremium, mmLapsed } from '../lib/premium';
import { payCheckout } from '../services/api';
import { useLang, useT } from '../lib/i18n';
import { PromptPayCheckout } from './PromptPayCheckout';

// Every line here is checked against what the code actually gates. Selling a
// feature a buyer cannot reach is the one mistake a pricing page must not make,
// so anything not enforced somewhere in this repo is not listed as a feature.
//
// Gold and Premium unlock exactly the same thing: applyPaidPlan writes
// plan:'premium' for both and only `tier` differs, and nothing anywhere reads
// `tier` for access. The difference between the two is the billing, which is
// what the Premium column now says instead of implying extra features.
// Every line here is checked against what the code actually gates. Selling a
// feature a buyer cannot reach is the one mistake a pricing page must not make,
// so anything not enforced somewhere in this repo is not listed as a feature.
//
// One item per line, never two joined with a plus. A buyer scanning a column
// counts what they get; "Super Cycle Time & Duration - schedule + seasonality" reads as one thing
// and is two, and the shorter list undersells what is behind the paywall.
//
// Gold and Premium unlock exactly the same thing: applyPaidPlan writes
// plan:'premium' for both and only `tier` differs, and nothing anywhere reads
// `tier` for access. The difference between the two is the billing, which is
// what the Premium column says instead of implying extra features.
export const PLANS = [
  {
    id: 'free', name: 'Free', price: '฿0', per: 'ตลอดไป', perEn: 'forever', cta: 'สมัครฟรี', ctaEn: 'Start free', hot: false,
    feats: [
      'ภาพรวมตลาดทุก Asset ทั่วโลก',
      'วิเคราะห์รายตัว Technical',
      'วิเคราะห์รายตัว Fundamental',
      'Scanner คัดกรองหุ้นทั่วโลก',
      'ตัวกรองพื้นฐาน',
      'ข่าวรายวัน',
      'บทวิเคราะห์ข่าวด้วย AI',
      'ปฏิทิน IPO · งบการเงิน · ปันผล',
      'ถอดรหัสนักลงทุน (ชาร์ตย่อ)',
      'Watchlist สูงสุด 10 รายการ',
    ],
    featsEn: [
      'Global market overview, every asset',
      'Per-asset Technical analysis',
      'Per-asset Fundamental analysis',
      'Global stock scanner',
      'Basic filters',
      'Daily news',
      'AI news analysis',
      'IPO · Earnings · Dividends calendar',
      'Human Design (summary chart)',
      'Watchlist up to 10',
    ],
  },
  {
    id: 'premium', name: 'Gold', price: '฿359', per: '/ เดือน', perEn: '/ month', cta: 'อัปเกรด Gold', ctaEn: 'Upgrade Gold', hot: true,
    feats: [
      // Track Port stays off the list while its page is closed. Selling an
      // entitlement the buyer cannot reach is worse than a shorter list; put it
      // back when the page reopens.
      'ทุกอย่างในแพ็กเกจ Free',
      'Bubble Watch — ฟองสบู่ 200 ปี',
      'สภาพคล่องดอลลาร์แบบสดจาก FRED',
      'NowcastLab — ความน่าจะเป็นก่อนข่าวออก 13 ข่าว',
      'ผลกระทบต่อสินทรัพย์รายตัว ก่อนข่าวออก',
      'Super Cycle Time & Duration — สถิติ seasonality 8 สินทรัพย์',
      'สถิติ Gap หลังงบ 31 ปี',
      'ตาราง Gap รายวัน',
      'Quant Watchlist',
      'บทเรียนตลาดรายวัน',
      'Options OI · CME Group',
      'Human Design ฉบับเต็ม',
      'พอร์ตแนะนำเฉพาะตัว',
      'Watchlist ไม่จำกัด',
    ],
    featsEn: [
      'Everything in Free',
      'Bubble Watch — two centuries of manias',
      'Live USD liquidity from FRED',
      'NowcastLab — probabilities for 13 US releases',
      'Per-asset impact before each release',
      'Super Cycle Time & Duration — seasonality across 8 instruments',
      '31 years of earnings-gap statistics',
      'The daily gap table',
      'Quant Watchlist',
      'Daily market digest',
      'Options OI · CME Group',
      'Full Human Design chart',
      'Personalised suggested portfolio',
      'Unlimited watchlist',
    ],
  },
  {
    id: 'yearly', name: 'Premium', price: '฿3,542', per: '/ ปี', perEn: '/ year', cta: 'อัปเกรด Premium', ctaEn: 'Upgrade Premium', hot: false,
    save: 'ประหยัด 18% — เท่ากับฟรี 2 เดือน', saveEn: 'Save 18% — 2 months free',
    feats: [
      'ทุกอย่างในแพ็กเกจ Gold ครบทุกฟีเจอร์',
      'จ่ายไม่ถึง 10 เดือน ใช้ 12 เดือน',
      'ประหยัด ฿766 ต่อปี',
      'ล็อกราคาตลอดอายุสมาชิก',
      'ไม่ต้องต่ออายุรายเดือน',
    ],
    featsEn: [
      'Every Gold feature, in full',
      'Pay under 10 months, use 12',
      'Save ฿766 a year',
      'Locked-in price for life',
      'No monthly renewal to remember',
    ],
  },
] as any[];

const LAND_FEATS = [
  { g: '⌁', c: '#00e0b0', t: 'วิเคราะห์ครบทุกมิติ', tEn: 'Analysis in every dimension', d: 'กราฟแท่งเทียน, อินดิเคเตอร์ 7 ตัว, งบการเงิน และ Valuation — หุ้นไทย, US, Crypto, ทอง, ETF, ตราสารหนี้', dEn: 'Candlesticks, 7 indicators, financials and valuation — Thai, US, crypto, gold, ETFs, bonds' },
  { g: '✦', c: '#8b5cf6', t: 'ถอดรหัสนักลงทุน', tEn: 'Decode the investor', d: 'ศาสตร์ Human Design วิเคราะห์พลังงานการตัดสินใจจากวันเกิด พร้อม Bodygraph และพอร์ตแนะนำเฉพาะตัว', dEn: 'Human Design reads your decision-making energy from your birth data, with a Bodygraph and a personalized portfolio' },
  { g: '◎', c: '#37c8f0', t: 'Scanner อัจฉริยะ', tEn: 'Smart Scanner', d: 'คัดกรองหุ้นทั่วโลก ด้วย — โครงสร้างราคา, โครงสร้างเวลา, มูลค่า, ปันผล, โมเมนตัม, คะแนนนักวิเคราะห์', dEn: 'Screen global stocks by price structure, time structure, value, dividend, momentum and analyst score' },
  { g: '▤', c: '#f0c75a', t: 'ปฏิทินครบวงจร', tEn: 'All-in-one calendar', d: 'IPO ทั่วโลก, ประกาศงบรายสัปดาห์, วันขึ้น XD — เลือกประเทศและช่วงเวลาได้', dEn: 'Global IPOs, weekly earnings, ex-dividend dates — filter by country and period' },
  // soon: the page behind this card is closed, so the card says so rather than
  // describing something a reader cannot reach. Drop the flag when it reopens.
  { g: '◈', c: '#ff8a3c', soon: true, t: 'Track Port จาก MT5 (EA)', tEn: 'Track Port from MT5 (EA)', d: 'เชื่อมบัญชี MetaTrader 5 ดู GAIN, Drawdown, Win Rate, และผลประกอบการของ EA ที่ใช้ AI และทีม AI Quant ในการบริหารจัดการพอร์ต ทุกบัญชีในจอเดียว', dEn: 'Connect MetaTrader 5 accounts to see GAIN, drawdown, win rate, and how EAs managed by AI and the AI Quant team are performing — every account on one screen' },
  { g: '◐', c: '#ff3b5c', t: 'ข่าว + AI วิเคราะห์', tEn: 'News + AI analysis', d: 'ฟีดข่าวพร้อมบทวิเคราะห์เชิงลึก Transmission Chain, Scenario และสัญญาณรายสินทรัพย์', dEn: 'News feed with deep analysis: transmission chains, scenarios and per-asset signals' },
];

/**
 * `user` is what tells this page whether it is talking to a visitor or to
 * somebody already signed in. Without it every call to action here read
 * "Log in / Start free" to a member who was already logged in.
 */
export function LandingPage({ onAuth, onEnter, user, onUpgrade }: any) {
  const { lang } = useLang();
  const t = useT();
  const en = lang === 'en';
  // "Already paying" excludes the trial on purpose: a reader on day 3 of the
  // trial is exactly who the upgrade buttons are for.
  const paid = !!(user && user.plan === 'premium' && user.tier && user.tier !== 'Trial');
  return (
    <div className="mm-landing" data-screen-label="Landing">
      <header className="mm-land-nav">
        <div className="mm-logo" style={{ padding: 0 }}>
          <img className="mm-logo-img" src="/brand/logo-mark.png" alt="Master of the Market" />
          <div className="mm-logo-txt">
            <strong>MASTER</strong>
            <span><em>of the</em> MARKET</span>
          </div>
        </div>
        {/* Somebody already signed in has nothing to log into and nothing to
            sign up for; offering both was the site failing to recognise its own
            member. They get the way in, and an upgrade route if they are not
            already on a paid plan. */}
        <div className="mm-top-actions">
          {user ? (
            <React.Fragment>
              {!paid && <button className="mm-btn ghost" onClick={onUpgrade}>{t('⚡ อัปเกรด', '⚡ Upgrade')}</button>}
              <button className="mm-btn primary" onClick={onEnter}>{t('เข้าใช้งาน →', 'Open app →')}</button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <button className="mm-btn ghost" onClick={() => onAuth('login')}>{t('เข้าสู่ระบบ', 'Log in')}</button>
              <button className="mm-btn primary" onClick={() => onAuth('signup')}>{t('สมัครฟรี', 'Start free')}</button>
            </React.Fragment>
          )}
        </div>
      </header>

      <section className="mm-hero-land">
        <span className="mm-chip mm-hero-chip">{t('✦ ใหม่ — ถอดรหัสนักลงทุนด้วยศาสตร์ Human Design', '✦ New — decode the investor with Human Design')}</span>
        {en
          ? <h1>Understand yourself before the market<br />and opportunity becomes yours</h1>
          : <h1>เข้าใจตัวเองก่อนเข้าใจตลาด<br />แล้วโอกาสจะเป็นของคุณ</h1>}
        <p>{t('แพลตฟอร์มวิเคราะห์การลงทุนที่รวม Fundamental, Technical และ Human Design ไว้ในที่เดียว — หุ้นไทย หุ้นโลก Crypto ทองคำ ETF ครบจบ', 'An investment-analysis platform that unites Fundamental, Technical and Human Design — Thai & global stocks, crypto, gold and ETFs, all in one place')}</p>
        {/* The 3-day trial offer means nothing to somebody who already has an
            account - they are either using that trial or have spent it. */}
        <div className="mm-hero-cta">
          {user
            ? <button className="mm-btn primary big" onClick={onEnter}>{t('เข้าใช้งาน →', 'Open app →')}</button>
            : <button className="mm-btn primary big" onClick={() => onAuth('signup')}>{t('ลงทะเบียนรับสิทธิ์ Premium 3 วัน', 'Get 3 days of Premium free')}</button>}
          {user
            ? (!paid && <button className="mm-btn ghost big" onClick={onUpgrade}>{t('⚡ อัปเกรดแพ็กเกจ', '⚡ Upgrade plan')}</button>)
            : <button className="mm-btn ghost big" onClick={onEnter}>{t('เยี่ยมชมเว็บไซต์ →', 'Explore the app →')}</button>}
        </div>
        <div className="mm-hero-stats mono">
          <span><b>9542+</b>{t('สินทรัพย์ทั่วโลก', 'global assets')}</span>
          <i></i>
          <span><b>15</b>{t('หน้าเครื่องมือ', 'tool pages')}</span>
          <i></i>
          <span><b>5</b>Investor Types</span>
        </div>
      </section>

      <section className="mm-land-feats">
        {LAND_FEATS.map((f, i) => (
          <div className="mm-land-feat glass" key={f.t} style={{ ['--fc' as any]: f.c, animationDelay: `${i * 70}ms` }}>
            <span className="mm-land-glyph mono" style={{ color: f.c }}>{f.g}</span>
            {/* Sits with the title, not floating in a corner: a reader scanning
                the six cards reads the name and the availability together. */}
            <b>{en ? f.tEn : f.t}{(f as any).soon && <span className="mm-feat-soon mono">{t('เร็ว ๆ นี้', 'SOON')}</span>}</b>
            <p>{en ? f.dEn : f.d}</p>
          </div>
        ))}
      </section>

      <section className="mm-land-pricing">
        <h2>{t('เลือกแพ็กเกจของคุณ', 'Choose your plan')}</h2>
        <p className="dim">{t('เริ่มฟรีได้เลย อัปเกรดเมื่อพร้อม — ยกเลิกได้ทุกเมื่อ', 'Start free, upgrade when ready — cancel anytime')}</p>
        <div className="mm-plans">
          {PLANS.map(p => (
            <div className={`mm-plan glass ${p.hot ? 'hot' : ''}`} key={p.id}>
              {p.hot && <span className="mm-plan-tag">{t('คุ้มที่สุด', 'Best value')}</span>}
              {p.save && <span className="mm-plan-tag gold">{en ? p.saveEn : p.save}</span>}
              <b className="mm-plan-name">{p.name}</b>
              <div className="mm-plan-price"><strong className="mono">{p.price}</strong><span className="dim">{en ? p.perEn : p.per}</span></div>
              <ul>
                {(en ? p.featsEn : p.feats).map((f: string) => <li key={f}>{f}</li>)}
              </ul>
              {/* Every card used to open the sign-up form, so a logged-in member
                  pressing "Upgrade Gold" was asked to create a second account.
                  Signed in, the paid cards open the upgrade flow and the Free
                  card just lets them in. */}
              {(() => {
                const free = p.id === 'free';
                if (!user) return <button className={`mm-btn ${p.hot ? 'primary' : 'ghost'} big w100`} onClick={() => onAuth('signup')}>{en ? p.ctaEn : p.cta}</button>;
                if (free) return <button className="mm-btn ghost big w100" onClick={onEnter}>{t('เข้าใช้งาน →', 'Open app →')}</button>;
                if (paid) return <button className="mm-btn ghost big w100" disabled>{t('เป็นสมาชิกอยู่แล้ว', 'Already a member')}</button>;
                return <button className={`mm-btn ${p.hot ? 'primary' : 'ghost'} big w100`} onClick={() => onUpgrade(p.id === 'yearly' ? 'y' : 'm')}>{en ? p.ctaEn : p.cta}</button>;
              })()}
            </div>
          ))}
        </div>
      </section>

      <footer className="mm-land-foot dim">
        {/* The "demo data is simulated" line dated from before the feeds were
            wired up and undersold a product now running on live prices, live
            MT5 and live news. The not-advice disclaimer stays - that one is not
            about the data being real, it is about what the reader does with it.
            "May be delayed or incomplete" is kept because some panels do fall
            back to bundled figures when a provider is unreachable. */}
        {t('Master of the Market © 2026 · ข้อมูลตลาดแบบเรียลไทม์จากผู้ให้บริการข้อมูล อาจมีความล่าช้าหรือคลาดเคลื่อน · ไม่ใช่คำแนะนำการลงทุน',
           'Master of the Market © 2026 · Live market data from third-party providers, may be delayed or incomplete · Not investment advice')}
        {/* Real pages under public/, not SPA routes: a policy has to be
            readable by someone who is not signed in and by a crawler that
            runs no JavaScript. Plain links, so they open as documents. */}
        <span className="mm-land-legal">
          <a href="/privacy">{t('นโยบายความเป็นส่วนตัว', 'Privacy Policy')}</a>
          <a href="/terms">{t('ข้อตกลงการใช้งาน', 'Terms of Service')}</a>
        </span>
      </footer>
    </div>
  );
}

// ---- Premium gate ----
export function PremiumGate({ user, onUpgrade, onAuth, title, desc, children }: any) {
  const t = useT();
  if (mmIsPremium(user)) return children;
  const lapsed = mmLapsed(user);
  return (
    <div className="mm-gate glass">
      <div className="mm-gate-lock">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="4" y="11" width="16" height="10" rx="2.5"></rect><path d="M8 11V7a4 4 0 018 0v4"></path></svg>
      </div>
      <h2>{title}</h2>
      <p className="dim">{lapsed === 'paid'
        ? t(`แพ็กเกจ ${user.tier} ของคุณหมดอายุแล้ว — ต่ออายุเพื่อใช้งานต่อเนื่อง`, `Your ${user.tier} plan has expired — renew to keep using it`)
        : lapsed === 'trial'
          ? t('สิทธิ์ทดลองใช้ Premium 3 วันของคุณหมดอายุแล้ว — อัปเกรดเพื่อใช้งานต่อเนื่อง', 'Your 3-day Premium trial has expired — upgrade to keep using it')
          : desc}</p>
      <div className="mm-port-actions" style={{ justifyContent: 'center' }}>
        {user
          ? <button className="mm-btn primary big" onClick={onUpgrade}>{lapsed === 'paid'
              ? t('⚡ ต่ออายุ — Gold ฿359/เดือน หรือ Premium ฿3,542/ปี', '⚡ Renew — Gold ฿359/mo or Premium ฿3,542/yr')
              : t('⚡ อัปเกรด — Gold ฿359/เดือน หรือ Premium ฿3,542/ปี', '⚡ Upgrade — Gold ฿359/mo or Premium ฿3,542/yr')}</button>
          : <React.Fragment>
              <button className="mm-btn primary big" onClick={() => onAuth('signup')}>{t('สมัครรับสิทธิ์ Premium ฟรี 3 วัน', 'Get 3 days of Premium free')}</button>
              <button className="mm-btn ghost big" onClick={() => onAuth('login')}>{t('เข้าสู่ระบบ', 'Log in')}</button>
            </React.Fragment>}
      </div>
      <span className="mm-gate-note mono dim">{lapsed === 'paid' ? 'PLAN EXPIRED' : lapsed === 'trial' ? 'TRIAL EXPIRED' : 'PREMIUM FEATURE'}</span>
    </div>
  );
}

// ---- Under-maintenance gate ----
// Shown in place of a page that is being worked on. Admins pass straight
// through, because the owner still has to build the thing customers are being
// told to wait for.
//
// Deliberately not a locked/PRO gate: this is not "pay to unlock", and dressing
// it up as one would read as a sales wall to someone who already paid. It says
// the feature is not finished, and offers a way back rather than leaving the
// reader on a dead end.
// onNav is optional: with it the gate stands in for a whole page and offers a
// way out, without it the gate is one section inside a page that otherwise
// works, and a "back to dashboard" button would be telling the reader to leave
// a page they have every reason to stay on.
export function MaintenanceGate({ user, onNav, title, children }: any) {
  const t = useT();
  if (user?.isAdmin) return children;
  return (
    <div className="mm-gate glass">
      <div className="mm-gate-wrench">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14.7 6.3a4 4 0 105.4 5.4l-2.1-2.1-1.2.3-1.4-1.4.3-1.2z"></path>
          <path d="M14.5 12.5L5.8 21.2a2 2 0 01-2.8-2.8l8.7-8.7"></path>
        </svg>
      </div>
      <h2>{title}</h2>
      <p className="dim">
        {t('ฟีเจอร์นี้อยู่ระหว่างปรับปรุง ยังไม่พร้อมใช้งาน',
           'This feature is being improved and is not ready yet')}
        <br />
        {t('ขออภัยในความไม่สะดวก — เปิดให้ใช้เมื่อไหร่จะแจ้งให้ทราบทาง LINE Official',
           'Sorry for the inconvenience — we will announce it on our LINE Official when it opens')}
      </p>
      {onNav && (
        <div className="mm-port-actions" style={{ justifyContent: 'center' }}>
          <button className="mm-btn primary big" onClick={() => onNav('dashboard')}>
            {t('กลับไปภาพรวมตลาด', 'Back to Dashboard')}
          </button>
        </div>
      )}
      <span className="mm-gate-note mono dim">UNDER MAINTENANCE</span>
    </div>
  );
}

// ---- Upgrade modal ----
export function UpgradeModal({ user, setUser, onClose, initialBill, stripeReady }: any) {
  const t = useT();
  const { lang } = useLang();
  const [done, setDone] = useState(false);
  // Opens on whichever card the reader pressed. Defaulting to Gold meant
  // somebody who chose Premium landed on the monthly plan and had to switch
  // back - the site quietly overruling the choice they just made.
  const [bill, setBill] = useState(initialBill === 'y' ? 'y' : 'm');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [pp, setPp] = useState(false);
  const checkout = async () => {
    setLoading(true); setErr('');
    const r = await payCheckout(bill === 'y' ? 'premium' : 'gold', user?.email);
    if (r.url) { window.location.href = r.url; return; }   // → real Stripe Checkout
    setLoading(false);
    // Points at PromptPay, which is configured and works. The old copy sent the
    // customer to the demo button, which no longer exists outside development -
    // and named a server environment variable at them, which means nothing to
    // somebody trying to pay.
    setErr(r.error === 'no_key'
      ? t('ยังไม่เปิดรับบัตรเครดิตในขณะนี้ — กรุณาชำระผ่านพร้อมเพย์ด้านบน', 'Card payments are not available yet — please use PromptPay above')
      : (t('เริ่มชำระเงินไม่สำเร็จ: ', 'Could not start checkout: ') + r.error));
  };
  const demoUpgrade = () => {
    setUser({ ...user, plan: 'premium', tier: bill === 'y' ? 'Premium' : 'Gold', billing: bill === 'y' ? 'yearly' : 'monthly', trialEnd: null });
    setDone(true);
    setTimeout(onClose, 1400);
  };
  const p = bill === 'y' ? PLANS[2] : PLANS[1];
  return (
    <div className="mm-modal-bg" onClick={onClose}>
      <div className="mm-modal glass" onClick={e => e.stopPropagation()}>
        <button className="mm-modal-x" onClick={onClose}>✕</button>
        <div className="mm-modal-orb"></div>
        {done ? (
          <div className="mm-upgrade-done">
            <span className="mm-upgrade-check">✓</span>
            <h2>{t('ยินดีต้อนรับสู่ ', 'Welcome to ')}{bill === 'y' ? 'Premium' : 'Gold'}!</h2>
            <p className="dim">{t('ปลดล็อกทุกฟีเจอร์แล้ว ขอให้ลงทุนอย่างเฉียบคม 🐯', 'All features unlocked — invest sharp 🐯')}</p>
          </div>
        ) : (
          <React.Fragment>
            <h2>{t('อัปเกรดเป็น ', 'Upgrade to ')}{bill === 'y' ? t('Premium (รายปี)', 'Premium (yearly)') : t('Gold (รายเดือน)', 'Gold (monthly)')}</h2>
            <div className="mm-bill-toggle">
              <button className={bill === 'm' ? 'on' : ''} onClick={() => setBill('m')}>{t('Gold · รายเดือน', 'Gold · monthly')}</button>
              <button className={bill === 'y' ? 'on' : ''} onClick={() => setBill('y')}>{t('Premium · รายปี', 'Premium · yearly')} <em>-17%</em></button>
            </div>
            <div className="mm-plan-price"><strong className="mono">{p.price}</strong><span className="dim">{lang === 'en' ? p.perEn : p.per}</span>{bill === 'y' && <span className="mm-bill-note mono">{t('≈ ฿249/เดือน', '≈ ฿249/mo')}</span>}</div>
            <ul className="mm-upgrade-list">
              {(lang === 'en' ? p.featsEn : p.feats).slice(1).map((f: string) => <li key={f}>{f}</li>)}
            </ul>
            {err && <div className="mm-err">{err}</div>}
            <button className="mm-btn primary big w100" onClick={() => setPp(true)}>{t('⚡ ชำระผ่านพร้อมเพย์ (โอน + แนบสลิป)', '⚡ Pay with PromptPay (transfer + slip)')}</button>
            {/* Only when the server actually has a Stripe key. Without one this
                button waited, failed, and told the customer to pay another way -
                an offer the site could not honour, next to one it could. */}
            {stripeReady && (
              <button className="mm-btn ghost w100" style={{ marginTop: 8 }} onClick={checkout} disabled={loading}>{loading ? t('กำลังไปหน้าชำระเงิน…', 'Going to checkout…') : t('บัตรเครดิต / Stripe', 'Credit card / Stripe')}</button>
            )}
            {/* Development builds only. Shipped, this handed a paying customer a
                free Gold/Premium badge for the session. It does not persist -
                the next load pulls the real plan from the server - but it sat
                inside the checkout reading "get it without paying", which does
                not belong next to a real price. */}
            {import.meta.env.DEV && (
              <button className="mm-btn ghost w100" style={{ marginTop: 8 }} onClick={demoUpgrade}>{t('ทดลองแบบเดโม่ (ไม่คิดเงินจริง) · เฉพาะโหมดพัฒนา', 'Try demo (no real charge) · dev only')}</button>
            )}
          </React.Fragment>
        )}
      </div>
      {pp && (
        <PromptPayCheckout
          kind="plan"
          item={{ plan: bill === 'y' ? 'premium' : 'gold' }}
          title={t('อัปเกรด', 'Upgrade') + ' ' + (bill === 'y' ? 'Premium' : 'Gold')}
          onClose={() => setPp(false)}
          onDone={() => setUser({ ...user, plan: 'premium', tier: bill === 'y' ? 'Premium' : 'Gold', billing: bill === 'y' ? 'yearly' : 'monthly', trialEnd: null })}
        />
      )}
    </div>
  );
}

// ---- Mobile bottom nav ----
export function BottomNav({ page, onNav, user }: any) {
  const { lang } = useLang();
  const ref = useRef<HTMLElement>(null);
  const drag = useRef({ active: false, moved: false, startX: 0, startScroll: 0 });

  // Trackpad/mouse wheel → horizontal scroll (vertical wheel still scrolls the bar).
  const onWheel = (e: React.WheelEvent<HTMLElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.currentTarget.scrollLeft += e.deltaY;
    }
  };

  // Mouse drag → grab-and-pan on PC. Touch uses native `touch-action: pan-x`
  // scrolling (smoother + momentum), so we ignore touch/pen pointers here.
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el || e.pointerType !== 'mouse') return;
    drag.current = { active: true, moved: false, startX: e.clientX, startScroll: el.scrollLeft };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el || !drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) {
      drag.current.moved = true;
      el.setPointerCapture?.(e.pointerId);
    }
    if (drag.current.moved) el.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = () => { drag.current.active = false; };

  // Keep the active page's button in view when navigating.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.querySelector<HTMLButtonElement>('button.on')
      ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [page]);

  return (
    <nav
      ref={ref}
      className="mm-bottomnav glass"
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
    >
      {NAV.map(n => {
        // Dimmed, not badged: this bar is icon-width on a phone and a chip
        // would crowd the label out. The gate still explains it on arrival -
        // this only stops the row looking as ready as the ones beside it.
        const soon = (n as any).soon && !user?.isAdmin;
        return (
          <button
            key={n.id}
            className={`${page === n.id ? 'on' : ''}${soon ? ' soon' : ''}`}
            // Ignore the click that ends a drag so dragging never fires navigation.
            onClick={() => { if (!drag.current.moved) onNav(n.id); }}
          >
            <NavIcon d={n.icon}></NavIcon>
            <span>{lang === 'en' ? n.en : n.th}</span>
          </button>
        );
      })}
    </nav>
  );
}
